import { FileUp, Loader2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { apiErrorMessage } from '@/i18n'
import { useCatalogSync, type CatalogSyncResult } from './api'

const MAX_DOC = 1 << 20

/**
 * Ürün reposunun katalog belgesini (klinik: api/catalog/clinic.json ya da kurulu imajda `api catalog`) içe alır.
 * Önce fark gösterilir; belgede olmayan kayıt silinmez, pasiflenir.
 */
export function CatalogSyncModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const sync = useCatalogSync()
  const [doc, setDoc] = useState<{ product: string; body: unknown; name: string } | null>(null)
  const [plan, setPlan] = useState<CatalogSyncResult | null>(null)

  const load = async (file: File | undefined) => {
    setPlan(null)
    setDoc(null)
    if (!file) return
    if (file.size > MAX_DOC) return toast.error(t('updates.catalog.tooLarge'))
    try {
      const body = JSON.parse(await file.text()) as { product?: unknown }
      if (typeof body.product !== 'string' || !body.product) throw new Error('product')
      setDoc({ product: body.product, body, name: file.name })
      setPlan(await sync.mutateAsync({ product: body.product, doc: body, dryRun: true }))
    } catch (e) {
      toast.error(
        e instanceof SyntaxError || (e instanceof Error && e.message === 'product')
          ? t('updates.catalog.invalid')
          : apiErrorMessage(e),
      )
    }
  }

  const apply = async () => {
    if (!doc) return
    try {
      const r = await sync.mutateAsync({ product: doc.product, doc: doc.body, dryRun: false })
      toast.success(t('updates.catalog.applied', { product: r.product }))
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  const p = plan?.plan
  const sections = p
    ? ([
        [
          'addModules',
          (p.add_modules ?? []).map(
            (m) => `${m.code}${m.is_core ? ' (core)' : ''}${m.requires?.length ? ` → ${m.requires.join(', ')}` : ''}`,
          ),
          'success',
        ],
        [
          'updateModules',
          (p.update_modules ?? []).map(
            (m) => `${m.code}${m.is_core ? ' (core)' : ''}${m.requires?.length ? ` → ${m.requires.join(', ')}` : ''}`,
          ),
          'info',
        ],
        ['deactivateModules', p.deactivate_modules ?? [], 'danger'],
        ['addFeatures', (p.add_features ?? []).map((f) => `${f.code} (${f.module})`), 'success'],
        ['updateFeatures', (p.update_features ?? []).map((f) => `${f.code} (${f.module})`), 'info'],
        ['deactivateFeatures', p.deactivate_features ?? [], 'danger'],
      ] as const)
    : []
  const empty = sections.every(([, list]) => list.length === 0)

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!sync.isPending}
      size="lg"
      icon={<RefreshCw />}
      title={t('updates.catalog.title')}
      description={t('updates.catalog.hint')}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void apply()} disabled={!plan || empty || sync.isPending}>
            {sync.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('updates.catalog.apply')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="hover:bg-muted/40 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-4 text-sm">
          <FileUp className="text-muted-foreground size-5" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{doc ? doc.name : t('updates.catalog.pick')}</span>
          <input
            type="file"
            accept="application/json,.json"
            className="sr-only"
            onChange={(e) => void load(e.target.files?.[0])}
          />
        </label>
        {plan ? (
          <div className="space-y-3">
            <div className="text-muted-foreground text-xs">
              {plan.product}
              {plan.version ? ` · ${plan.version}` : ''}
            </div>
            {empty ? (
              <p className="text-success text-sm">{t('updates.catalog.equal')}</p>
            ) : (
              sections
                .filter(([, list]) => list.length > 0)
                .map(([key, list, tone]) => (
                  <div key={key} className="space-y-1">
                    <div className="text-sm font-medium">{t(`updates.catalog.${key}`)}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map((x) => (
                        <Badge key={x} tone={tone} className="font-mono">
                          {x}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
